import { countEmAbertoActiveFilters } from './utils';
import { normalizeTipoOSSelection } from './tipoOsFilterUtils';

export const BOARD_FILTER_SCOPE = {
  EM_ABERTO: 'emAberto',
  TECNICOS: 'tecnicos',
  AMBOS: 'ambos',
};

const SHARED_ARRAY_FILTER_KEYS = [
  'cidade',
  'bairro',
  'cliente',
  'tipoOS',
  'sla',
  'equipamento',
  'defeitoDescricao',
  'defeitoAlert',
  'defeitoClassification',
  'defeitoSeverity',
];

function normalizePanelFiltersForApply(panelFilters) {
  return {
    ...panelFilters,
    tipoOS: normalizeTipoOSSelection(panelFilters.tipoOS || []),
  };
}

function mergeSharedFiltersForDisplay(columnFilters, technicianFilters) {
  const tech = technicianFiltersToPanelShape(technicianFilters);
  const merged = { ...columnFilters };
  SHARED_ARRAY_FILTER_KEYS.forEach((key) => {
    const columnValues = columnFilters[key] || [];
    const techValues = tech?.[key] || [];
    if (columnValues.length === 0 && techValues.length === 0) return;
    const combined = [...columnValues, ...techValues];
    merged[key] = key === 'tipoOS' ? normalizeTipoOSSelection(combined) : [...new Set(combined)];
  });
  return merged;
}

export function technicianFiltersToPanelShape(tech) {
  if (!tech) return null;
  return {
    retorno: tech.retorno ?? null,
    cidade: tech.cidade || [],
    bairro: tech.bairro || [],
    cliente: tech.cliente || [],
    tipoOS: tech.tipoOS || [],
    sla: tech.sla || [],
    equipamento: tech.equipamento || [],
    status: [],
    tecnicoInicial: [],
    previsao: tech.previsao || [],
    osFuturaRange: tech.osFuturaRange ?? null,
    defeitoDescricao: tech.defeitoDescricao || [],
    defeitoAlert: tech.defeitoAlert || [],
    defeitoClassification: tech.defeitoClassification || [],
    defeitoSeverity: tech.defeitoSeverity || [],
  };
}

export function pickSharedPanelFiltersForTechnician(panel, existingTech) {
  return {
    ...existingTech,
    retorno: panel.retorno,
    cidade: [...panel.cidade],
    bairro: [...panel.bairro],
    cliente: [...panel.cliente],
    tipoOS: [...panel.tipoOS],
    sla: [...panel.sla],
    equipamento: [...panel.equipamento],
    previsao: [...(panel.previsao || [])],
    osFuturaRange: panel.osFuturaRange ?? null,
    defeitoDescricao: [...panel.defeitoDescricao],
    defeitoAlert: [...panel.defeitoAlert],
    defeitoClassification: [...panel.defeitoClassification],
    defeitoSeverity: [...panel.defeitoSeverity],
  };
}

export function getDisplayFiltersForScope(scope, columnFilters, technicianFilters) {
  if (scope === BOARD_FILTER_SCOPE.EM_ABERTO) {
    return {
      ...columnFilters,
      tipoOS: normalizeTipoOSSelection(columnFilters.tipoOS || []),
      previsao: [],
      osFuturaRange: null,
    };
  }
  if (scope === BOARD_FILTER_SCOPE.TECNICOS) {
    const tech = technicianFiltersToPanelShape(technicianFilters);
    return {
      ...tech,
      tipoOS: normalizeTipoOSSelection(tech.tipoOS || []),
    };
  }
  const merged = mergeSharedFiltersForDisplay(columnFilters, technicianFilters);
  return {
    ...merged,
    previsao: technicianFilters.previsao || [],
    osFuturaRange: technicianFilters.osFuturaRange ?? null,
    retorno:
      technicianFilters.retorno != null && usesTechnicianRetorno(scope)
        ? technicianFilters.retorno
        : columnFilters.retorno,
  };
}

export function applyPanelFiltersToScope(
  panelFilters,
  scope,
  setColumnFilters,
  setTechnicianFilters,
  technicianFilters,
  columnFilters
) {
  const panel = normalizePanelFiltersForApply(panelFilters);
  if (scope === BOARD_FILTER_SCOPE.EM_ABERTO) {
    const { previsao, osFuturaRange, ...columnShape } = panel;
    setColumnFilters(columnShape);
    return;
  }
  if (scope === BOARD_FILTER_SCOPE.TECNICOS) {
    setTechnicianFilters(pickSharedPanelFiltersForTechnician(panel, technicianFilters));
    return;
  }
  const { previsao, osFuturaRange, retorno, ...shared } = panel;
  setColumnFilters({
    ...columnFilters,
    ...shared,
    retorno: columnFilters.retorno,
  });
  setTechnicianFilters(pickSharedPanelFiltersForTechnician(panel, technicianFilters));
}

export function clearFiltersForScope(scope, setColumnFilters, setTechnicianFilters, initialColumn, initialTech) {
  if (scope === BOARD_FILTER_SCOPE.EM_ABERTO || scope === BOARD_FILTER_SCOPE.AMBOS) {
    setColumnFilters(initialColumn);
  }
  if (scope === BOARD_FILTER_SCOPE.TECNICOS || scope === BOARD_FILTER_SCOPE.AMBOS) {
    setTechnicianFilters(initialTech);
  }
}

function countTechnicianSharedFilters(filters) {
  if (!filters) return 0;
  const retornoCount =
    filters.retorno === 'sim' || filters.retorno === 'nao'
      ? 1
      : filters.retorno && typeof filters.retorno === 'object'
        ? 1
        : 0;
  return (
    retornoCount +
    (filters.previsao?.length || 0) +
    (filters.cidade?.length || 0) +
    (filters.bairro?.length || 0) +
    (filters.cliente?.length || 0) +
    (filters.tipoOS?.length || 0) +
    (filters.sla?.length || 0) +
    (filters.equipamento?.length || 0) +
    (filters.osFuturaRange ? 1 : 0) +
    (filters.defeitoDescricao?.length || 0) +
    (filters.defeitoAlert?.length || 0) +
    (filters.defeitoClassification?.length || 0) +
    (filters.defeitoSeverity?.length || 0)
  );
}

export function usesTechnicianRetorno(scope) {
  return scope === BOARD_FILTER_SCOPE.TECNICOS || scope === BOARD_FILTER_SCOPE.AMBOS;
}

export function getActiveFilterCountForScope(scope, columnFilters, technicianFilters) {
  if (scope === BOARD_FILTER_SCOPE.EM_ABERTO) {
    return countEmAbertoActiveFilters(columnFilters);
  }
  if (scope === BOARD_FILTER_SCOPE.TECNICOS) {
    return countTechnicianSharedFilters(technicianFilters);
  }
  return Math.max(
    countEmAbertoActiveFilters(columnFilters),
    countTechnicianSharedFilters(technicianFilters)
  );
}
